import { injectable } from 'inversify'

/**
 * One scope per **list** that can be narrowed — the same shape as
 * `FeedbackScope`, and for the same reason: several surfaces need identical
 * machinery, so it is one keyed mechanism rather than one class per surface.
 *
 * Naming matches the feedback scopes (`feature.surface`) so a page that later
 * splits into sub-lists already has a scope per sub-list.
 *
 * Only the core/admin scopes live here — a feature adds its own scope (and
 * the matching `ScopeDefinition` below) when it introduces a filterable list.
 */
export const FilterScopes = ['admin.users', 'admin.audit'] as const
export type FilterScope = (typeof FilterScopes)[number]

/**
 * What a control *is*, not what it means. The meaning — which items a choice
 * keeps — lives with the domain, in the list's own Presenter, because only that
 * knows the shape of the thing being filtered. This file is deliberately free of
 * predicates for that reason.
 */
export type FilterKind =
  /** Free text, matched however the list's Presenter decides. */
  | { kind: 'text'; placeholderKey: string }
  /** One of a fixed set, or none. Options are ids the Presenter interprets. */
  | { kind: 'choice'; options: readonly { id: string; labelKey: string }[] }
  /**
   * Like `choice`, but the options are data the workspace creates (task types,
   * locations…), not a compile-time set — so the list Presenter supplies them
   * at build time via `buildFilterBarVm`'s `lookups` argument, keyed by this
   * filter's id. A distinct kind rather than an optional field on `choice` so
   * the type system forces every reader to decide, and a config-only choice can
   * never silently render an empty dropdown.
   */
  | { kind: 'lookup' }
  /** On or off. */
  | { kind: 'toggle' }
  /**
   * A min, a max, or both — "more than 3", "less than 5", "3 to 5". Stored as
   * one string (`FiltersRepository` already carries any string), encoded
   * `"min.."` / `"..max"` / `"min..max"`; see `matchesNumber`.
   */
  | { kind: 'number' }
  /**
   * A from, a to, or both — same range encoding as `'number'`
   * (`"min.."` / `"..max"` / `"min..max"`), bounds being ISO `YYYY-MM-DD`,
   * both inclusive. See `matchesDate`.
   */
  | { kind: 'date' }

export type FilterDefinition = FilterKind & {
  /** Stable id: the key the selection is stored under, and what a Presenter reads. */
  id: string
  labelKey: string
  /**
   * Which column's header shows this filter as a funnel icon, instead of the
   * bar — the header-sort precedent (`sortInHeaders`), one level down: a
   * number filter reads as "narrow by this column" and belongs beside the
   * column it narrows, not among the bar's whole-row controls.
   */
  column?: string
}

export type SortDefinition = {
  id: string
  labelKey: string
  /**
   * What the direction control says while *this* sort is chosen. "A→Z" is right
   * for a name and meaningless for a date or a count, and which words describe
   * the two ends is a property of the sort key rather than of the bar. Omitted
   * falls back to `filters.ascending` / `filters.descending`.
   */
  ascendingLabelKey?: string
  descendingLabelKey?: string
  /**
   * Which direction a *first* click on this sort produces. Names go A→Z, but
   * nobody asking for a trail by time wants the oldest entry first, and nobody
   * sorting by a count is looking for the smallest. Defaults to ascending.
   */
  startAscending?: boolean
}

export type ScopeDefinition = {
  filters: readonly FilterDefinition[]
  /**
   * Omit all three of `sorts`/`defaultSortId`/`pageSize` together for a scope
   * that neither sorts nor paginates — a view-segment scope driving a section
   * directly off `values`, never through `buildFilterBarVm`/`paginate`, is the
   * shape. Declaring one without the other two is not meaningful and is not
   * checked for.
   */
  sorts?: readonly SortDefinition[]
  /** Which sort applies before the user picks one. */
  defaultSortId?: string
  /**
   * Which direction that default sort starts in. Ascending suits a name; a trail
   * of events is read newest-first, so it starts descending.
   */
  defaultAscending?: boolean
  /**
   * True when this list is a table whose column headers carry the sort. The bar
   * then leaves out its sort dropdown and direction button, because the same
   * axis on two controls at once is the defect this config exists to prevent.
   *
   * Only set it where *every* declared sort has a column to click. A sort with
   * no header, on a bar with no dropdown, is unreachable.
   */
  sortInHeaders?: boolean

  /**
   * How many rows a page holds. Every list that paginates declares one: a list
   * that cannot grow today is a list nobody has stress-tested, and the cost of
   * a page size that is never reached is zero.
   */
  pageSize?: number
}

/**
 * Declarative half of filtering, separated from FiltersRepository exactly as
 * NavigationConfig is separated from NavigationRepository: this owns *what
 * filters exist*, the Repository owns *which are active*.
 *
 * Injectable rather than a bare module import so a test can bind a smaller table
 * without touching anything that reads it.
 */
@injectable()
export class FiltersConfig {
  private readonly scopes: Record<FilterScope, ScopeDefinition> = {
    'admin.audit': {
      filters: [
        { id: 'text', kind: 'text', labelKey: 'filters.search', placeholderKey: 'filters.audit.searchPlaceholder' },
        {
          id: 'actorFilter',
          kind: 'text',
          labelKey: 'admin.audit.actorHeading',
          placeholderKey: 'filters.audit.actorPlaceholder',
          column: 'actor',
        },
      ],
      // One sort *key* with two directions, not two keys. Declaring 'newest' and
      // 'oldest' as separate sorts put the same axis on two controls at once —
      // the dropdown and the direction button — which could then contradict each
      // other. Newest first is the only order a trail is read in, hence the
      // descending default; ascending is for following one sequence forwards.
      sorts: [
        {
          id: 'when',
          labelKey: 'filters.audit.when',
          ascendingLabelKey: 'filters.audit.oldest',
          descendingLabelKey: 'filters.audit.newest',
          startAscending: false,
        },
        { id: 'actor', labelKey: 'filters.audit.actor' },
      ],
      defaultSortId: 'when',
      // An audit trail is the one list that only ever grows, and the only one
      // read in pages rather than searched.
      pageSize: 50,
      defaultAscending: false,
      sortInHeaders: true,
    },
    'admin.users': {
      filters: [
        { id: 'text', kind: 'text', labelKey: 'filters.search', placeholderKey: 'filters.users.searchPlaceholder' },
        {
          id: 'role',
          kind: 'choice',
          labelKey: 'filters.users.role',
          options: [
            { id: 'user', labelKey: 'admin.users.roleUser' },
            { id: 'admin', labelKey: 'admin.users.roleAdmin' },
          ],
        },
        // Positive like every other toggle — on narrows. Deliberately *not*
        // "hide deleted by default": this page exists partly to show what a
        // deleted account left behind, so hiding those rows would hide half its
        // purpose.
        { id: 'deleted', kind: 'toggle', labelKey: 'filters.users.deletedOnly' },
        { id: 'contentRange', kind: 'number', labelKey: 'filters.users.content', column: 'content' },
      ],
      sorts: [
        { id: 'email', labelKey: 'filters.users.email' },
        {
          id: 'content',
          labelKey: 'filters.users.content',
          ascendingLabelKey: 'filters.sort.fewestFirst',
          descendingLabelKey: 'filters.sort.mostFirst',
          startAscending: false,
        },
      ],
      defaultSortId: 'email',
      pageSize: 25,
      sortInHeaders: true,
    },
  }

  definitionFor(scope: FilterScope): ScopeDefinition {
    return this.scopes[scope]
  }
}
